import { Link } from "react-router-dom";
import { getProjectUsers } from "../utils/getProjectUsers";
import { getDepartments } from "../utils/getDepartments";
import { getEmployeesByDepartments } from "../utils/getEmployeesByDepartments";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { UserCheck, MoreVertical, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, GripVertical, ArrowUpDown, X, Eye, ExternalLink, Download, Bug, Sparkles, FlaskConical, Phone, Search } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChrome, faFirefox } from "@fortawesome/free-brands-svg-icons";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { uploadFiles } from "../utils/uploadFiles";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatProjectTitle } from "../utils/formatProjectTitle";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const getProjectFirebaseConfig = (project = {}) => ({
  firebaseProjectId: project.firebaseProjectId || "",
});

export default function ProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const [leProjects, setLeProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("main");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showLeForm, setShowLeForm] = useState(false);
  const [editingLeProject, setEditingLeProject] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isDragMode, setIsDragMode] = useState(false); // NEW: Drag mode toggle
  const [searchQuery, setSearchQuery] = useState("");
  const menuRef = useRef(null);
  
  // Confirm dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  
  // View Users modal state
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedProjectUsers, setSelectedProjectUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    link: "",
    uatLink: "", // UAT (staging) link for the project
    langsmithApiKey: "",
    reportBugLink: "",
    requestFeaturesLink: "",
    dialNumber: "", // Phone number for dial call (e.g., for IVR projects)
    videos: [], // array of { url?, file?, title } for display; saved as { url, title }[]
    images: [],
    status: "active",
    workspaceKey: "", // NEW: Workspace path for fetching users
    department: [], // NEW: Department field (array for multiple selection)
    selectedUsers: [], // NEW: Selected users (can be emails or employee IDs)
    division: [], // Division field (array for multiple selection)
    isDownload: false, // When on, project page shows Download instead of Open App
  });
  const [leFormData, setLeFormData] = useState({
    title: "",
    link: "",
    langsmithApiKey: "",
    reportBugLink: "",
    requestFeaturesLink: "",
    selectedUsers: [],
    status: "active",
  });
  
  // State for employees filtered by selected departments
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const projectsRef = collection(db, "workspaces", "ai-apps-dashboard", "projects");
  const leProjectsRef = collection(db, "workspaces", "ai-apps-dashboard", "le-small-projects");
  const membersRef = collection(db, "workspaces", "ai-apps-dashboard", "users");

  // Default division options for the Division field (Add/Edit Project)
  const DIVISION_OPTIONS = [
    { value: "Health", label: "Health" },
    { value: "System", label: "System" },
    { value: "Locums", label: "Locums" },
  ];

  // Fetch data - sort by sequence if available, fallback to createdAt
  const fetchData = async () => {
    setLoading(true);
    try {
      const projectsSnap = await getDocs(projectsRef);
      const leProjectsSnap = await getDocs(leProjectsRef);
      const membersSnap = await getDocs(membersRef);

      let projectsData = projectsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      let leProjectsData = leProjectsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const membersData = membersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Sort by sequence if available, otherwise by createdAt
      projectsData.sort((a, b) => {
        // If both have sequence, sort by sequence
        if (typeof a.sequence === 'number' && typeof b.sequence === 'number') {
          return a.sequence - b.sequence;
        }
        // If only one has sequence, prioritize it
        if (typeof a.sequence === 'number') return -1;
        if (typeof b.sequence === 'number') return 1;
        // Otherwise sort by createdAt
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateA - dateB;
      });
      leProjectsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      setProjects(projectsData);
      setLeProjects(leProjectsData);
      setTeamMembers(membersData);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unique departments from employees collection
  const fetchDepartments = async () => {
    const result = await getDepartments();
    if (result.ok) {
      setDepartments(result.departments);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDepartments();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (openMenuId) {
        setOpenMenuId(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openMenuId]);

  // Toggle dropdown menu
  const toggleMenu = (projectId, buttonRect) => {
    if (openMenuId === projectId) {
      setOpenMenuId(null);
    } else {
      setOpenMenuId(projectId);
      if (buttonRect) {
        setMenuPosition({
          top: buttonRect.bottom + 8,
          left: buttonRect.right - 192 // 192px is the width of the dropdown (w-48 = 12rem = 192px)
        });
      }
    }
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = (project) => {
    setProjectToDelete(project);
    setShowConfirmDialog(true);
    setOpenMenuId(null);
  };

  // Drag and Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder projects
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = projects.findIndex((p) => p.id === active.id);
    const newIndex = projects.findIndex((p) => p.id === over.id);

    // Reorder locally first for instant UI update
    const reorderedProjects = arrayMove(projects, oldIndex, newIndex);
    setProjects(reorderedProjects);

    // Update sequence in Firestore for drag & drop ordering
    try {
      const batch = writeBatch(db);
      
      reorderedProjects.forEach((project, index) => {
        const projectRef = doc(db, "workspaces", "ai-apps-dashboard", "projects", project.id);
        batch.update(projectRef, { sequence: index, updatedAt: serverTimestamp() });
      });

      await batch.commit();
      console.log("✅ Project order saved! (sequence field added)");
    } catch (err) {
      console.error("❌ Error updating project order:", err);
      // Revert on error
      fetchData();
      console.error("❌ Failed to save order. Please try again.");
    }
  };

  // Soft Delete - Move to trash
  const handleDelete = async () => {
    if (!projectToDelete) return;

    try {
      console.log("🗑️ Starting soft delete for:", projectToDelete.title);
      
      // Step 1: Add to trash collection FIRST (before deleting)
      const trashRef = collection(db, "workspaces", "ai-apps-dashboard", "trash");
      const trashDoc = await addDoc(trashRef, {
        ...projectToDelete,
        deletedAt: serverTimestamp(),
        originalCollection: "projects",
        originalProjectId: projectToDelete.id, // Keep original ID for reference
      });
      
      console.log("✅ Project saved to trash with ID:", trashDoc.id);

      // Step 2: Only AFTER successful save to trash, remove from projects
      const projectRef = doc(db, "workspaces", "ai-apps-dashboard", "projects", projectToDelete.id);
      await deleteDoc(projectRef);
      
      console.log("✅ Project removed from active projects");
      
      // Step 3: Update UI
      setProjects((prevProjects) => prevProjects.filter((p) => p.id !== projectToDelete.id));
      
      // Close dialog
      setShowConfirmDialog(false);
      setProjectToDelete(null);
      
      console.log("✅ Soft delete completed successfully");
      
      // ✅ Data is safely stored in trash collection - can be restored later!
    } catch (err) {
      console.error("❌ Error moving project to trash:", err);
      console.error("❌ Failed to move project to trash. Please try again.");
      
      // Close dialog even on error
      setShowConfirmDialog(false);
      setProjectToDelete(null);
    }
  };

  // Filter to show only active projects
  const activeProjects = projects.filter((p) => p.status === "active");

  const matchesProjectName = (project) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const raw = String(project?.title || "").toLowerCase();
    const display = String(formatProjectTitle(project?.title) || "").toLowerCase();
    return raw.includes(q) || display.includes(q);
  };

  const filteredActiveProjects = activeProjects.filter(matchesProjectName);
  const filteredLeProjects = leProjects.filter(matchesProjectName);
  
  // Stats
  const totalProjects = activeProjects.length;
  const totalMembers = teamMembers.length;
  const activeMembers = teamMembers.filter((m) => m.role === "user").length;
  const completedProjects = projects.filter((p) => p.status === "completed").length;

  // Pagination calculations (search applies in normal mode; reorder shows all)
  const listedProjects = isDragMode ? activeProjects : filteredActiveProjects;
  const totalPages = Math.ceil(listedProjects.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = isDragMode ? activeProjects : listedProjects.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, viewMode]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  const openLeForm = (project = null) => {
    if (project) {
      setEditingLeProject(project);
      setLeFormData({
        title: project.title || "",
        link: project.link || "",
        langsmithApiKey: project.langsmithApiKey || "",
        reportBugLink: project.reportBugLink || "",
        requestFeaturesLink: project.requestFeaturesLink || "",
        selectedUsers: project.selectedUsers || [],
        status: project.status || "active",
      });
    } else {
      setEditingLeProject(null);
      setLeFormData({
        title: "",
        link: "",
        langsmithApiKey: "",
        reportBugLink: "",
        requestFeaturesLink: "",
        selectedUsers: [],
        status: "active",
      });
    }
    setShowLeForm(true);
  };

  const handleLeChange = (e) => {
    const { name, value } = e.target;
    setLeFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLeSave = async (e) => {
    e.preventDefault();
    setSavingLeProject(true);

    try {
      const payload = {
        title: leFormData.title,
        link: leFormData.link || "",
        langsmithApiKey: leFormData.langsmithApiKey || "",
        reportBugLink: leFormData.reportBugLink || "",
        requestFeaturesLink: leFormData.requestFeaturesLink || "",
        selectedUsers: leFormData.selectedUsers || [],
        status: leFormData.status || "active",
        updatedAt: serverTimestamp(),
      };

      if (editingLeProject) {
        const ref = doc(db, "workspaces", "ai-apps-dashboard", "le-small-projects", editingLeProject.id);
        await updateDoc(ref, payload);
        setLeProjects((prev) => prev.map((item) => (item.id === editingLeProject.id ? { ...item, ...payload } : item)));
      } else {
        const newDocRef = await addDoc(leProjectsRef, {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setLeProjects((prev) => [{ id: newDocRef.id, ...payload }, ...prev]);
      }

      setShowLeForm(false);
    } catch (err) {
      console.error("❌ Error saving LE project:", err);
    } finally {
      setSavingLeProject(false);
    }
  };

  const handleLeDelete = async (project) => {
    const shouldDelete = window.confirm(`Delete LE project "${project.title}"?`);
    if (!shouldDelete) return;

    try {
      const ref = doc(db, "workspaces", "ai-apps-dashboard", "le-small-projects", project.id);
      await deleteDoc(ref);
      setLeProjects((prev) => prev.filter((item) => item.id !== project.id));
    } catch (err) {
      console.error("❌ Error deleting LE project:", err);
    }
  };

  // Fetch employees when departments change
  useEffect(() => {
    const fetchEmployees = async () => {
      if (formData.department && formData.department.length > 0) {
        console.log("🔄 Fetching employees for departments:", formData.department);
        setLoadingEmployees(true);
        const result = await getEmployeesByDepartments(formData.department);
        if (result.ok) {
          console.log(`✅ Loaded ${result.employees.length} employees`);
          setAvailableEmployees(result.employees);
        } else {
          console.error("❌ Failed to fetch employees");
          setAvailableEmployees([]);
        }
        setLoadingEmployees(false);
      } else {
        console.log("⚠️ No departments selected, clearing employees");
        setAvailableEmployees([]);
      }
    };
    
    // Only fetch if form is open
    if (showForm) {
      fetchEmployees();
    }
  }, [formData.department, showForm]);

  // Normalize videos to { url, title }[] for form (backward compat: project.videos may be string[] or { url, title }[])
  const normalizeVideosForForm = (videos) => {
    if (!videos || !Array.isArray(videos)) return [];
    return videos.map((v, i) => {
      if (typeof v === "string") return { url: v, title: `Video ${i + 1}` };
      return { url: v.url || v, title: v.title || `Video ${i + 1}` };
    });
  };

  // Form handlers
  const openForm = async (project = null) => {
    if (project) {
      setEditingProject(project);
      const departments = project.department || [];
      setFormData({
        title: project.title,
        description: project.description || "",
        link: project.link || "",
        uatLink: project.uatLink || "",
        langsmithApiKey: project.langsmithApiKey || "",
        reportBugLink: project.reportBugLink || "",
        requestFeaturesLink: project.requestFeaturesLink || "",
        dialNumber: project.dialNumber || "",
        videos: normalizeVideosForForm(project.videos),
        images: project.images || [],
        status: project.status || "active",
        workspaceKey: project.workspaceKey || "",
        department: departments,
        selectedUsers: project.selectedUsers || [],
        division: project.division || [],
        isDownload: Boolean(project.isDownload),
      });
      
      // Fetch employees for the project's departments
      if (departments.length > 0) {
        setLoadingEmployees(true);
        const result = await getEmployeesByDepartments(departments);
        if (result.ok) {
          setAvailableEmployees(result.employees);
        }
        setLoadingEmployees(false);
      }
    } else {
      setEditingProject(null);
      setFormData({
        title: "",
        description: "",
        link: "",
        uatLink: "",
        langsmithApiKey: "",
        reportBugLink: "",
        requestFeaturesLink: "",
        dialNumber: "",
        videos: [],
        images: [],
        status: "active",
        workspaceKey: "",
        department: [],
        selectedUsers: [],
        division: [],
        isDownload: false,
      });
      setAvailableEmployees([]);
    }
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === "videos") {
      const newEntries = Array.from(files).map((file) => ({
        file,
        title: file.name.replace(/\.[^/.]+$/, "") || file.name || "Untitled",
      }));
      setFormData({
        ...formData,
        videos: [...(formData.videos || []), ...newEntries],
      });
      e.target.value = "";
      return;
    }
    setFormData({
      ...formData,
      [name]: name === "images" ? [...formData.images, ...files] : [...files],
    });
  };

  const handleRemoveImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index),
    });
  };

  const handleVideoTitleChange = (index, title) => {
    setFormData({
      ...formData,
      videos: formData.videos.map((v, i) => (i === index ? { ...v, title } : v)),
    });
  };

  const handleRemoveVideo = (index) => {
    setFormData({
      ...formData,
      videos: formData.videos.filter((_, i) => i !== index),
    });
  };

  const [saving, setSaving] = useState(false);  // For form saving only
  const [savingLeProject, setSavingLeProject] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let imageUrls = formData.images;
      let videoItems = []; // final: array of { url, title }

      if (formData.images.some((f) => f instanceof File)) {
        imageUrls = await uploadFiles(
          formData.images.filter((f) => f instanceof File),
          "images"
        );
      }

      const videoEntries = formData.videos || [];
      const toUpload = videoEntries.filter((v) => v.file);
      const existing = videoEntries.filter((v) => v.url && !v.file);
      if (toUpload.length > 0) {
        const files = toUpload.map((v) => v.file);
        const urls = await uploadFiles(files, "videos");
        toUpload.forEach((v, i) => {
          videoItems.push({ url: urls[i], title: v.title || `Video ${i + 1}` });
        });
      }
      videoItems = [...existing.map((v) => ({ url: v.url, title: v.title || "Video" })), ...videoItems];

      const payload = {
        title: formData.title,
        description: formData.description,
        link: formData.link,
        uatLink: formData.uatLink || "",
        langsmithApiKey: formData.langsmithApiKey || "",
        reportBugLink: formData.reportBugLink || "",
        requestFeaturesLink: formData.requestFeaturesLink || "",
        dialNumber: formData.dialNumber || "",
        images: imageUrls,
        videos: videoItems,
        status: formData.status,
        workspaceKey: formData.workspaceKey || "",
        department: formData.department || [],
        selectedUsers: formData.selectedUsers || [],
        division: formData.division || [],
        isDownload: Boolean(formData.isDownload),
        updatedAt: new Date(),
      };

      let projectId;

      if (editingProject) {
        // Update existing project
        const ref = doc(db, "workspaces", "ai-apps-dashboard", "projects", editingProject.id);
        await updateDoc(ref, { ...payload, updatedAt: serverTimestamp() });
        projectId = editingProject.id;
        
        // Optimistically update local state without refetching
        setProjects((prevProjects) =>
          prevProjects.map((p) =>
            p.id === editingProject.id ? { ...p, ...payload } : p
          )
        );
      } else {
        // Add new project - assign highest sequence + 1 for new projects only
        const maxSequence = projects.length > 0 
          ? Math.max(...projects.map(p => typeof p.sequence === 'number' ? p.sequence : -1)) 
          : -1;
        
        const newDocRef = await addDoc(projectsRef, {
          ...payload,
          sequence: maxSequence + 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        
        projectId = newDocRef.id;
        
        // Optimistically add to local state without refetching
        setProjects((prevProjects) => [
          ...prevProjects,
          { id: newDocRef.id, ...payload, sequence: maxSequence + 1, createdAt: new Date() }
        ]);
      }

      // Save selected users to devfolio-users collection
      const devfolioUsersRef = collection(db, "workspaces", "ai-apps-dashboard", "devfolio-users");
      
      // Get existing devfolio users to check for duplicates
      const existingUsersSnap = await getDocs(devfolioUsersRef);
      const existingUserEmails = new Set();
      const existingUserDocs = {};
      
      existingUsersSnap.docs.forEach((doc) => {
        const data = doc.data();
        if (data.email) {
          const normalizedEmail = data.email.toLowerCase().trim();
          existingUserEmails.add(normalizedEmail);
          existingUserDocs[normalizedEmail] = { id: doc.id, data: data };
        }
      });

      // Get all users who were previously assigned to this project (for editing)
      const previouslyAssignedUsers = editingProject?.selectedUsers || [];
      const currentSelectedUsers = (formData.selectedUsers || []).filter(u => u !== "__select_all__");
      
      // Find users to remove (were assigned before but not now)
      const usersToRemove = previouslyAssignedUsers.filter(
        email => !currentSelectedUsers.includes(email)
      );
      
      // Remove project from users who are no longer assigned
      for (const userEmail of usersToRemove) {
        const normalizedEmail = userEmail.toLowerCase().trim();
        if (existingUserEmails.has(normalizedEmail)) {
          const existingDoc = existingUserDocs[normalizedEmail];
          const currentProjects = existingDoc.data.projects || [];
          const updatedProjects = currentProjects.filter(p => p !== formData.title);
          
          const userRef = doc(db, "workspaces", "ai-apps-dashboard", "devfolio-users", existingDoc.id);
          await updateDoc(userRef, {
            projects: updatedProjects,
            updatedAt: serverTimestamp(),
          });
        }
      }

      // Add/update selected users (for project assignment)
      // This includes both employees from the filtered list and custom email addresses
      if (currentSelectedUsers.length > 0) {
        // Fetch employees collection to get user info for custom emails
        const employeesRef = collection(db, "cynetdatabase", "root", "employees");
        const employeesSnap = await getDocs(employeesRef);
        
        // Create a map of all employees by email (for custom email lookup)
        const allEmployeesMap = {};
        employeesSnap.docs.forEach((doc) => {
          const data = doc.data();
          if (data.email) {
            const normalizedEmail = data.email.toLowerCase().trim();
            allEmployeesMap[normalizedEmail] = {
              id: doc.id,
              email: data.email,
              name: data.name?.full || data.name?.given || data.fullName || "",
              fullName: data.name?.full || data.fullName || data.name?.given || "",
              department: data.department || "",
              ...data
            };
          }
        });
        
        for (const userEmail of currentSelectedUsers) {
          const normalizedEmail = userEmail.toLowerCase().trim();
          
          // Try to find employee data from availableEmployees first (filtered by department)
          let employee = availableEmployees.find(
            emp => (emp.email || "").toLowerCase().trim() === normalizedEmail
          );
          
          // If not found in filtered list, try to find in all employees (for custom emails)
          if (!employee && allEmployeesMap[normalizedEmail]) {
            employee = allEmployeesMap[normalizedEmail];
          }
          
          // If still not found, it's a custom email - create basic user entry
          if (!employee) {
            // Custom email - create user entry with minimal info
            if (existingUserEmails.has(normalizedEmail)) {
              // Update existing user - add project to their projects array if not already there
              const existingDoc = existingUserDocs[normalizedEmail];
              const currentProjects = existingDoc.data.projects || [];
              
              if (!currentProjects.includes(formData.title)) {
                const userRef = doc(db, "workspaces", "ai-apps-dashboard", "devfolio-users", existingDoc.id);
                await updateDoc(userRef, {
                  projects: [...currentProjects, formData.title],
                  updatedAt: serverTimestamp(),
                });
              }
            } else {
              // Create new user entry with custom email
              await addDoc(devfolioUsersRef, {
                email: normalizedEmail,
                name: normalizedEmail.split('@')[0],
                fullName: normalizedEmail.split('@')[0],
                department: null,
                projects: [formData.title],
                isActive: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
            }
          } else {
            // Employee found - use employee data
            if (existingUserEmails.has(normalizedEmail)) {
              // Update existing user - add project to their projects array if not already there
              const existingDoc = existingUserDocs[normalizedEmail];
              const currentProjects = existingDoc.data.projects || [];
              
              if (!currentProjects.includes(formData.title)) {
                const userRef = doc(db, "workspaces", "ai-apps-dashboard", "devfolio-users", existingDoc.id);
                await updateDoc(userRef, {
                  projects: [...currentProjects, formData.title],
                  updatedAt: serverTimestamp(),
                });
              }
            } else {
              // Create new user entry
              await addDoc(devfolioUsersRef, {
                email: employee.email,
                name: employee.name || employee.fullName || "",
                fullName: employee.fullName || employee.name || "",
                department: employee.department || "",
                projects: [formData.title],
                isActive: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
            }
          }
        }
      }
      
      setShowForm(false);
      // ✅ No fetchData() call - no reload!
    } catch (err) {
      console.error("❌ Error saving project:", err);
      console.error("❌ Failed to save project. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const departmentOptions = departments.map((dept) => ({
    value: dept,
    label: dept,
  }));

  // Employee options for user selection
  const employeeOptions = availableEmployees.map((emp) => ({
    value: emp.email || emp.id,
    label: `${emp.fullName || emp.name || emp.email || "Unknown"} (${emp.email || ""})`,
    employee: emp, // Store full employee object
  }));

  // Add "Select All" option
  const allEmployeeOptions = [
    { value: "__select_all__", label: "Select All", isSelectAll: true },
    ...employeeOptions
  ];
  const leUserOptions = teamMembers
    .map((member) => {
      const email = member.email || member.workEmail || "";
      if (!email) return null;
      const fullName = member.fullName || member.name || email;
      return {
        value: email,
        label: `${fullName} (${email})`,
      };
    })
    .filter(Boolean);

  // Helper function to check if a value is an email
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Create options from selectedUsers that might include custom emails
  const getSelectedUserOptions = () => {
    return formData.selectedUsers.map(userValue => {
      // Check if it's an email (custom added) or from employee list
      if (isValidEmail(userValue)) {
        // Check if it's already in employeeOptions
        const existingOption = employeeOptions.find(opt => opt.value === userValue);
        if (existingOption) {
          return existingOption;
        }
        // Custom email - create option
        return {
          value: userValue,
          label: userValue,
          isCustomEmail: true
        };
      }
      // Find in employeeOptions
      return employeeOptions.find(opt => opt.value === userValue) || { value: userValue, label: userValue };
    }).filter(Boolean);
  };
  const navigate = useNavigate();

  // Handle View Users button click
  const handleViewUsers = async (project) => {
    setSelectedProject(project);
    setShowUsersModal(true);
    setLoadingUsers(true);
    setSelectedProjectUsers([]);

    try {
      const workspaceKey =
        project.workspaceKey ||
        project.title.toLowerCase().replace(/\s+/g, "-").trim();
      const projectFirebaseConfig = getProjectFirebaseConfig(project);

      const res = await getProjectUsers(workspaceKey, null, projectFirebaseConfig);
      
      if (res.ok) {
        // Use allUsers for admin view
        setSelectedProjectUsers(res.allUsers || res.users || []);
      } else {
        setSelectedProjectUsers([]);
      }
    } catch (err) {
      console.error("Error fetching project users:", err);
      setSelectedProjectUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh] text-gray-600 dark:text-gray-300">
        Loading dashboard...
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full">
      {/* Total Projects Table */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md p-6 overflow-hidden">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="flex items-center gap-2 shrink-0">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                {viewMode === "main" ? "Total Projects" : "LE Projects"}
              </h2>
              <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" title="Number of projects">
                {viewMode === "main" ? totalProjects : leProjects.length}
              </span>
              {viewMode === "main" && isDragMode && (
                <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 font-normal">
                  (Reorder Mode - Drag to change order)
                </span>
              )}
            </div>
            <div className="relative w-full max-w-md sm:w-[26rem] sm:max-w-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search project by name..."
                disabled={viewMode === "main" && isDragMode}
                className="w-full pl-9 pr-9 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(viewMode === "main" ? "le" : "main")}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition ${
                viewMode === "le"
                  ? "bg-violet-600 text-white hover:bg-violet-700"
                  : "bg-violet-100 text-violet-700 hover:bg-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:hover:bg-violet-900/50"
              }`}
            >
              {viewMode === "main" ? "LE Project" : "All Projects"}
            </button>
            {viewMode === "le" && (
              <button
                onClick={() => openLeForm()}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Plus size={16} /> Add LE Project
              </button>
            )}
            {viewMode === "main" && (
              <>
            <button
              onClick={() => setIsDragMode(!isDragMode)}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition ${
                isDragMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <ArrowUpDown size={16} />
              {isDragMode ? 'Exit Reorder' : 'Reorder Mode'}
            </button>
          <button
            onClick={() => openForm()}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus size={16} /> Add Project
          </button>
              </>
            )}
          </div>
        </div>

        {viewMode === "main" ? (
          <>
        <div className="overflow-x-auto rounded-lg">
          {isDragMode ? (
            // Drag & Drop enabled mode
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={currentProjects.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
          <table className="w-full border-collapse relative">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                      <th className="px-2 py-3 w-12"></th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Title</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Division</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Useful Links</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 relative">
              {currentProjects.map((p) => (
                      <SortableRow
                  key={p.id}
                        project={p}
                        teamMembers={teamMembers}
                        navigate={navigate}
                        toggleMenu={toggleMenu}
                        openMenuId={openMenuId}
                        menuRef={menuRef}
                        openForm={openForm}
                        openDeleteConfirm={openDeleteConfirm}
                        setOpenMenuId={setOpenMenuId}
                        menuPosition={menuPosition}
                        isDragMode={isDragMode}
                        handleViewUsers={handleViewUsers}
                      />
                    ))}

                    {currentProjects.length === 0 && (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                        >
                          {searchQuery.trim() ? "No projects match your search." : "No active projects found."}
                  </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          ) : (
            // Normal mode without drag & drop
            <table className="w-full border-collapse relative">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                  <th className="px-16 py-3 text-left text-sm font-semibold">Project Title</th>
                  <th className="px-20 py-3 text-left text-sm font-semibold">Division</th>
                  <th className="px-20 py-3 text-left text-sm font-semibold">Useful Links</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 relative">
                {currentProjects.map((p) => (
                  <NormalRow
                    key={p.id}
                    project={p}
                    teamMembers={teamMembers}
                    navigate={navigate}
                    toggleMenu={toggleMenu}
                    openMenuId={openMenuId}
                    menuRef={menuRef}
                    openForm={openForm}
                    openDeleteConfirm={openDeleteConfirm}
                    setOpenMenuId={setOpenMenuId}
                    menuPosition={menuPosition}
                    handleViewUsers={handleViewUsers}
                  />
              ))}

              {currentProjects.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                  >
                    {searchQuery.trim() ? "No projects match your search." : "No active projects found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>

        {/* Show total count in drag mode */}
        {isDragMode && projects.length > 0 && (
          <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
            Showing all {projects.length} projects for reordering
          </div>
        )}

        {/* Pagination Controls - Hide in drag mode */}
        {!isDragMode && listedProjects.length > 0 && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              Showing {startIndex + 1} to {Math.min(endIndex, listedProjects.length)} of {listedProjects.length} projects
            </div>
            
            {totalPages > 1 && (
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border ${
                  currentPage === 1
                    ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <ChevronLeft size={18} />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-3 py-1.5 text-gray-500 dark:text-gray-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        currentPage === page
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border ${
                  currentPage === totalPages
                    ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <ChevronRight size={18} />
              </button>
            </div>
            )}
          </div>
        )}
          </>
        ) : (
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full border-collapse relative">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold">Project Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Open App</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Bug Form</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Feature Form</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Users</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 relative">
                {filteredLeProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-medium">{project.title || "-"}</td>
                    <td className="px-4 py-3">
                      {isValidLink(project.link) ? (
                        <a href={project.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-green-600 hover:text-green-700">
                          <ExternalLink size={14} /> Open
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isValidLink(project.reportBugLink) ? (
                        <a href={project.reportBugLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700">
                          <Bug size={14} /> Open
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isValidLink(project.requestFeaturesLink) ? (
                        <a href={project.requestFeaturesLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700">
                          <Sparkles size={14} /> Open
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {Array.isArray(project.selectedUsers) ? project.selectedUsers.length : 0}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openLeForm(project)}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Edit LE project"
                        >
                          <Edit2 size={16} className="text-blue-500" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleLeDelete(project)}
                          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Delete LE project"
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredLeProjects.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                      {searchQuery.trim() ? "No projects match your search." : "No LE projects found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Project Modal */}
      <AnimatePresence>
        {showForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-5xl overflow-y-auto max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-100">
                {editingProject ? "Edit Project" : "Add New Project"}
              </h2>

              <form onSubmit={handleSave} className="space-y-6">
                {/* Row 1: Title & Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="active">Active</option>
                      <option value="comingSoon">Coming Soon</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                {/* Row 2.5: Division */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Division
                  </label>
                  <CreatableSelect
                    isMulti
                    name="division"
                    options={DIVISION_OPTIONS}
                    value={formData.division ? formData.division.map((div) => ({ value: div, label: div })) : []}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        division: selected ? selected.map((s) => s.value) : [],
                      })
                    }
                    isClearable
                    isSearchable
                    placeholder="Select or create divisions (e.g., Health, System, Locums)"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: document.documentElement.classList.contains("dark")
                          ? "#1f2937" // dark:bg-gray-800
                          : "#ffffff",
                        borderColor: state.isFocused ? "#6366f1" : "#4b5563",
                        borderRadius: "0.5rem",
                        minHeight: "44px",
                        boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "",
                        "&:hover": { borderColor: "#6366f1" },
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: document.documentElement.classList.contains("dark")
                          ? "#111827" // dark:bg-gray-900
                          : "#ffffff",
                        borderRadius: "0.5rem",
                        marginTop: "4px",
                        padding: "4px",
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "#4f46e5" // selected option = indigo-600
                          : state.isFocused
                            ? (document.documentElement.classList.contains("dark")
                              ? "#374151" // dark:hover = gray-700
                              : "#e0e7ff") // light:hover = indigo-100
                            : "transparent",
                        color: state.isSelected
                          ? "#ffffff"
                          : document.documentElement.classList.contains("dark")
                            ? "#f9fafb" // dark:text-gray-50
                            : "#111827", // text-gray-900
                        borderRadius: "0.375rem",
                        padding: "8px 12px",
                        cursor: "pointer",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#6366f1", // indigo
                        borderRadius: "0.375rem",
                        padding: "0 4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "white",
                        fontSize: "0.875rem",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "white",
                        ":hover": {
                          backgroundColor: "#4f46e5",
                          color: "white",
                        },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: document.documentElement.classList.contains("dark")
                          ? "#9ca3af"
                          : "#6b7280",
                      }),
                      input: (base) => ({
                        ...base,
                        color: document.documentElement.classList.contains("dark")
                          ? "#ffffff" // white text in dark mode
                          : "#111827", // dark text in light mode
                      }),
                    }}
                  />
                </div>

                {/* Description - Full Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Row 3: Project Link, UAT Link, LangSmith API key */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Link (Live)
                    </label>
                    <input
                      type="text"
                      name="link"
                      value={formData.link}
                      onChange={handleChange}
                      placeholder="Live project URL or NA"
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Enter URL to show in <strong>Live Projects</strong> tab. Write <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">NA</code> to show project without {formData.isDownload ? "Download Link" : "Open App"} button.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      UAT Link (Staging)
                    </label>
                    <input
                      type="text"
                      name="uatLink"
                      value={formData.uatLink}
                      onChange={handleChange}
                      placeholder="UAT / staging URL or NA"
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Enter URL to show in <strong>UAT Projects</strong> tab. Write <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">NA</code> to show project without {formData.isDownload ? "Download Link" : "Open App"} button.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      LangSmith API Key <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="langsmithApiKey"
                      value={formData.langsmithApiKey}
                      onChange={handleChange}
                      placeholder="lsv2_pt_... (project-specific key)"
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      LangSmith dashboard uses this key plus your <strong>Project Title</strong> to guess the tracing project name
                      (e.g. <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">Reverse Marketing</code> →{" "}
                      <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">Reverse_Marketing</code>).
                      EU tenant: set <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">LANGSMITH_ENDPOINT</code> on the Functions emulator/deploy env if needed.
                    </p>
                  </div>
                </div>

                {/* Download toggle — replaces Open App with Download on the project page */}
                <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Download
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When on, the project page shows <strong>Download Link</strong> instead of <strong>Open App</strong>.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.isDownload}
                    onClick={() => setFormData((f) => ({ ...f, isDownload: !f.isDownload }))}
                    className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
                      formData.isDownload ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 mt-0.5 rounded-full bg-white shadow transform transition ${
                        formData.isDownload ? "translate-x-5 ml-0.5" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>

                {/* Report Bug Link & Request Features Link */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Report Bug Link <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="reportBugLink"
                      value={formData.reportBugLink}
                      onChange={handleChange}
                      placeholder="e.g. Jira form URL or NA"
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Request Features Link <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="requestFeaturesLink"
                      value={formData.requestFeaturesLink}
                      onChange={handleChange}
                      placeholder="e.g. Jira form URL or NA"
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                {/* Dial Call Number - Only for IVR projects */}
                {formData.title && formData.title.toLowerCase().includes("ivr") && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Dial Call Number
                      </label>
                      <input
                        type="tel"
                        name="dialNumber"
                        value={formData.dialNumber}
                        onChange={handleChange}
                        placeholder="e.g., +1234567890"
                        className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Enter phone number to show a dial call button. Opens phone dialer when clicked.
                      </p>
                    </div>
                  </div>
                )}

                {/* Row 4: Workspace Path */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Workspace Path <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="workspaceKey"
                      value={formData.workspaceKey}
                      onChange={handleChange}
                      placeholder="Leave empty for default workspace"
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                      💡 <strong>Default:</strong> Uses <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">workspaces/ai-apps-dashboard</code>
                      <br />
                      <span className="text-[11px]">
                        <strong>Only fill this if using a different workspace!</strong>
                        <br />
                        Example: <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">workspaces/blindCv</code> or <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">IVR_Workspaces/main</code>
                        <br />
                        System will fetch users from <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">{"{path}"}/users</code> or <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded">{"{path}"}/profiles</code>
                      </span>
                    </p>
                  </div>
                </div>

                {/* Row 4: Images & Videos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Images
                    </label>
                  <input
                    type="file"
                    name="images"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  {formData.images?.length > 0 && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {formData.images.map((img, idx) => {
                        const src = img instanceof File ? URL.createObjectURL(img) : img;
                        return (
                          <div key={idx} className="relative">
                            <img
                              src={src}
                              alt=""
                              className="w-full h-24 object-contain rounded"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(idx)}
                              className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Videos
                    </label>
                    <input
                      type="file"
                      name="videos"
                      accept="video/*"
                      multiple
                      onChange={handleFileChange}
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {formData.videos?.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {formData.videos.map((v, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600"
                          >
                            <input
                              type="text"
                              value={v.title || ""}
                              onChange={(e) => handleVideoTitleChange(idx, e.target.value)}
                              placeholder="Video title"
                              className="flex-1 min-w-0 p-1.5 text-sm border rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                            />
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                              {v.file ? v.file.name : "Uploaded"}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveVideo(idx)}
                              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                              title="Remove video"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border rounded-lg dark:border-gray-600 dark:hover:bg-gray-700 dark:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    {saving ? "Saving..." : "Save Project"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add/Edit LE Project Modal */}
      <AnimatePresence>
        {showLeForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowLeForm(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-2xl overflow-y-auto max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-100">
                {editingLeProject ? "Edit LE Project" : "Add LE Project"}
              </h2>

              <form onSubmit={handleLeSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project Name</label>
                  <input
                    type="text"
                    name="title"
                    value={leFormData.title}
                    onChange={handleLeChange}
                    required
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Open App Link</label>
                  <input
                    type="text"
                    name="link"
                    value={leFormData.link}
                    onChange={handleLeChange}
                    placeholder="https://..."
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    LangSmith API Key <span className="text-gray-400 text-xs">(Internal only)</span>
                  </label>
                  <input
                    type="text"
                    name="langsmithApiKey"
                    value={leFormData.langsmithApiKey}
                    onChange={handleLeChange}
                    placeholder="lsv2_pt_..."
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Saved for admin/internal use only. This key is not shown in project lists for users.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bug Form Link</label>
                    <input
                      type="text"
                      name="reportBugLink"
                      value={leFormData.reportBugLink}
                      onChange={handleLeChange}
                      placeholder="https://..."
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Feature Form Link</label>
                    <input
                      type="text"
                      name="requestFeaturesLink"
                      value={leFormData.requestFeaturesLink}
                      onChange={handleLeChange}
                      placeholder="https://..."
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Users</label>
                  <CreatableSelect
                    isMulti
                    options={leUserOptions}
                    value={(leFormData.selectedUsers || []).map((value) => {
                      const option = leUserOptions.find((item) => item.value === value);
                      return option || { value, label: value };
                    })}
                    onChange={(selected) =>
                      setLeFormData((prev) => ({
                        ...prev,
                        selectedUsers: selected ? selected.map((s) => s.value) : [],
                      }))
                    }
                    isClearable
                    isSearchable
                    placeholder="Select users or type custom email"
                    className="basic-multi-select"
                    classNamePrefix="select"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowLeForm(false)}
                    className="px-4 py-2 border rounded-lg dark:border-gray-600 dark:hover:bg-gray-700 dark:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingLeProject}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    {savingLeProject ? "Saving..." : "Save LE Project"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setProjectToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Project?"
        message={`Are you sure you want to move "${projectToDelete?.title}" to trash? You can restore it later from the trash.`}
        confirmText="Move to Trash"
        cancelText="Cancel"
        type="danger"
      />

      {/* View Users Modal */}
      <AnimatePresence>
        {showUsersModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowUsersModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                  Users - {selectedProject?.title ? formatProjectTitle(selectedProject.title) : "N/A"}
                </h2>
                <button
                  onClick={() => setShowUsersModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X size={20} className="text-gray-600 dark:text-gray-300" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingUsers ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">Loading users...</p>
                  </div>
                ) : selectedProjectUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">No users found for this project.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedProjectUsers.map((user) => {
                      const profilePic =
                        user.photo ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          user.displayName || user.name || user.fullName || user.email || "U"
                        )}&background=random`;

                      return (
                        <div
                          key={user.id}
                          className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                        >
                          <img
                            src={profilePic}
                            alt={user.displayName || user.name || user.fullName || user.email}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white dark:border-gray-600"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 dark:text-gray-100 truncate">
                              {user.displayName || user.name || user.fullName || "Unnamed"}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {user.email || "—"}
                            </p>
                            {user.department && (
                              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                {user.department}
                              </p>
                            )}
                          </div>
                          {user.active !== undefined && (
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                user.active
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300"
                              }`}
                            >
                              {user.active ? "Active" : "Inactive"}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
     </div>
  );
}

// Helper to check if a link is valid (not empty, not "NA", not "na")
const isValidLink = (link) => {
  if (!link) return false;
  const trimmed = link.trim().toLowerCase();
  return trimmed && trimmed !== "na";
};

// Sortable Row Component for Drag & Drop
function SortableRow({ project, teamMembers, navigate, toggleMenu, openMenuId, menuRef, openForm, openDeleteConfirm, setOpenMenuId, menuPosition, handleViewUsers }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const displayTitle = formatProjectTitle(project.title);

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-gray-50 dark:hover:bg-gray-800 transition relative"
    >
      {/* Drag Handle */}
      <td className="px-2 py-3 text-center">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <GripVertical size={18} />
        </button>
      </td>

      {/* Title with Image */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <img
            src={(project.images && project.images[0]) || "/placeholder.png"}
            alt={displayTitle}
            className="w-16 h-12 object-contain rounded-lg"
          />
          <Link
            to={`/admin/projects/${project.id}`}
            onClick={async (e) => {
              e.preventDefault();

              try {
                console.log("🖱 Clicked project:", project.title);

                const workspaceKey =
                  project.workspaceKey ||
                  project.title.toLowerCase().replace(/\s+/g, "-").trim();
                const projectFirebaseConfig = getProjectFirebaseConfig(project);

                console.log("🧠 Detected workspace:", workspaceKey);

                const { getProjectUsers } = await import("../utils/getProjectUsers");
                const res = await getProjectUsers(workspaceKey, null, projectFirebaseConfig);

                localStorage.setItem("workspaceKey", workspaceKey);
                localStorage.setItem("projectUsers", JSON.stringify(res.users));
                localStorage.setItem("projectFirebaseConfig", JSON.stringify(projectFirebaseConfig));

                console.log(`✅ Stored ${res.users.length} users for ${workspaceKey}`);

                navigate(`/admin/projects/${project.id}`, { state: { project } });
              } catch (err) {
                console.error("🔥 Error fetching project users:", err);
                navigate(`/admin/projects/${project.id}`, { state: { project } });
              }
            }}
            className="text-gray-800 dark:text-gray-100 hover:text-indigo-600 transition font-medium"
          >
            {displayTitle}
          </Link>
        </div>
      </td>

      {/* Division */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {project.division && Array.isArray(project.division) && project.division.length > 0 ? (
            project.division.map((div, idx) => (
              <span
                key={idx}
                className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700 dark:bg-purple-700 dark:text-purple-100"
              >
                {div}
              </span>
            ))
          ) : (
            <span className="text-gray-400 dark:text-gray-500 text-sm">-</span>
          )}
        </div>
      </td>

      {/* Project Buttons */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button
              onClick={() => navigate(`/admin/access-management/${project.id}`)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40 hover:scale-110 transition-all duration-200"
            >
              <UserCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </button>
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
              Access Management
            </div>
          </div>
          {isValidLink(project.link) && (
            <div className="relative group">
              <a
                href={project.link}
                target="_blank"
                rel="noreferrer"
                download={project.isDownload || undefined}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 hover:scale-110 transition-all duration-200"
              >
                {project.isDownload ? (
                  <Download className="w-5 h-5" style={{ color: '#10B981' }} />
                ) : (
                  <ExternalLink className="w-5 h-5" style={{ color: '#10B981' }} />
                )}
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                {project.isDownload ? "Download Link" : "Open App"}
              </div>
            </div>
          )}
          {isValidLink(project.uatLink) && (
            <div className="relative group">
              <a
                href={project.uatLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:scale-110 transition-all duration-200"
              >
                <FlaskConical className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                UAT (Staging)
              </div>
            </div>
          )}
          {project.title && project.title.toLowerCase().includes("smart contact") && (
            <>
              <div className="relative group">
                <a
                  href="https://chromewebstore.google.com/detail/smart-contact-extension/mmiggdkmkkkmjdpfkmbkbdechhgihlph"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:scale-110 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faChrome} className="w-5 h-5" style={{ color: '#4285F4' }} />
                </a>
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                  Chrome Extension
                </div>
              </div>
              <div className="relative group">
                <a
                  href="https://smartcontact.cynetcorp.com/v1/public/firefox-extension/download"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 hover:scale-110 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faFirefox} className="w-5 h-5" style={{ color: '#FF7139' }} />
                </a>
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                  Firefox Extension
                </div>
              </div>
            </>
          )}
          {isValidLink(project.reportBugLink) && (
            <div className="relative group">
              <a
                href={project.reportBugLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 hover:scale-110 transition-all duration-200"
              >
                <Bug className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                Report Bug
              </div>
            </div>
          )}
          {isValidLink(project.requestFeaturesLink) && (
            <div className="relative group">
              <a
                href={project.requestFeaturesLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 hover:scale-110 transition-all duration-200"
              >
                <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                Request Features
              </div>
            </div>
          )}
          {project.dialNumber && project.dialNumber.trim() && (
            <div className="relative group">
              <a
                href={`tel:${project.dialNumber.trim()}`}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/40 hover:scale-110 transition-all duration-200"
              >
                <Phone className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                Call {project.dialNumber.trim()}
              </div>
            </div>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            project.status === "active"
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              : project.status === "comingSoon"
              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              : project.status === "completed"
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
          }`}
        >
          {project.status?.charAt(0).toUpperCase() + project.status?.slice(1) || "N/A"}
        </span>
      </td>

      {/* Action Menu */}
      <td className="px-4 py-3 text-right">
        <div className="relative">
          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              toggleMenu(project.id, rect);
            }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <MoreVertical size={18} className="text-gray-600 dark:text-gray-300" />
          </button>

          {openMenuId === project.id && createPortal(
            <div 
              ref={menuRef}
              className="fixed w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[9999]"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              <button
                onClick={() => {
                  openForm(project);
                  setOpenMenuId(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg transition-colors"
              >
                <Edit2 size={16} className="text-blue-500" />
                Edit Project
              </button>
              <button
                onClick={() => openDeleteConfirm(project)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg transition-colors"
              >
                <Trash2 size={16} className="text-red-500" />
                Delete Project
              </button>
            </div>,
            document.body
          )}
        </div>
      </td>
    </tr>
  );
}

// Normal Row Component (without drag & drop)
function NormalRow({ project, teamMembers, navigate, toggleMenu, openMenuId, menuRef, openForm, openDeleteConfirm, setOpenMenuId, menuPosition, handleViewUsers }) {
  const displayTitle = formatProjectTitle(project.title);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition relative">
      {/* Title with Image */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <img
            src={(project.images && project.images[0]) || "/placeholder.png"}
            alt={displayTitle}
            className="w-16 h-12 object-contain rounded-lg"
          />
          <Link
            to={`/admin/projects/${project.id}`}
            onClick={async (e) => {
              e.preventDefault();

              try {
                console.log("🖱 Clicked project:", project.title);

                const workspaceKey =
                  project.workspaceKey ||
                  project.title.toLowerCase().replace(/\s+/g, "-").trim();
                const projectFirebaseConfig = getProjectFirebaseConfig(project);

                console.log("🧠 Detected workspace:", workspaceKey);

                const { getProjectUsers } = await import("../utils/getProjectUsers");
                const res = await getProjectUsers(workspaceKey, null, projectFirebaseConfig);

                localStorage.setItem("workspaceKey", workspaceKey);
                localStorage.setItem("projectUsers", JSON.stringify(res.users));
                localStorage.setItem("projectFirebaseConfig", JSON.stringify(projectFirebaseConfig));

                console.log(`✅ Stored ${res.users.length} users for ${workspaceKey}`);

                navigate(`/admin/projects/${project.id}`, { state: { project } });
              } catch (err) {
                console.error("🔥 Error fetching project users:", err);
                navigate(`/admin/projects/${project.id}`, { state: { project } });
              }
            }}
            className="text-gray-800 dark:text-gray-100 hover:text-indigo-600 transition font-medium"
          >
            {displayTitle}
          </Link>
        </div>
      </td>

      {/* Division */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {project.division && Array.isArray(project.division) && project.division.length > 0 ? (
            project.division.map((div, idx) => (
              <span
                key={idx}
                className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700 dark:bg-purple-700 dark:text-purple-100"
              >
                {div}
              </span>
            ))
          ) : (
            <span className="text-gray-400 dark:text-gray-500 text-sm">-</span>
          )}
        </div>
      </td>

      {/* Project Buttons */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button
              onClick={() => navigate(`/admin/access-management/${project.id}`)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40 hover:scale-110 transition-all duration-200"
            >
              <UserCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </button>
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
              Access Management
            </div>
          </div>
          {isValidLink(project.link) && (
            <div className="relative group">
              <a
                href={project.link}
                target="_blank"
                rel="noreferrer"
                download={project.isDownload || undefined}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 hover:scale-110 transition-all duration-200"
              >
                {project.isDownload ? (
                  <Download className="w-5 h-5" style={{ color: '#10B981' }} />
                ) : (
                  <ExternalLink className="w-5 h-5" style={{ color: '#10B981' }} />
                )}
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                {project.isDownload ? "Download Link" : "Open App"}
              </div>
            </div>
          )}
          {isValidLink(project.uatLink) && (
            <div className="relative group">
              <a
                href={project.uatLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:scale-110 transition-all duration-200"
              >
                <FlaskConical className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                UAT (Staging)
              </div>
            </div>
          )}
          {project.title && project.title.toLowerCase().includes("smart contact") && (
            <>
              <div className="relative group">
                <a
                  href="https://chromewebstore.google.com/detail/smart-contact-extension/mmiggdkmkkkmjdpfkmbkbdechhgihlph"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:scale-110 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faChrome} className="w-5 h-5" style={{ color: '#4285F4' }} />
                </a>
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                  Chrome Extension
                </div>
              </div>
              <div className="relative group">
                <a
                  href="https://smartcontact.cynetcorp.com/v1/public/firefox-extension/download"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 hover:scale-110 transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faFirefox} className="w-5 h-5" style={{ color: '#FF7139' }} />
                </a>
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                  Firefox Extension
                </div>
              </div>
            </>
          )}
          {isValidLink(project.reportBugLink) && (
            <div className="relative group">
              <a
                href={project.reportBugLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 hover:scale-110 transition-all duration-200"
              >
                <Bug className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                Report Bug
              </div>
            </div>
          )}
          {isValidLink(project.requestFeaturesLink) && (
            <div className="relative group">
              <a
                href={project.requestFeaturesLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 hover:scale-110 transition-all duration-200"
              >
                <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                Request Features
              </div>
            </div>
          )}
          {project.dialNumber && project.dialNumber.trim() && (
            <div className="relative group">
              <a
                href={`tel:${project.dialNumber.trim()}`}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/40 hover:scale-110 transition-all duration-200"
              >
                <Phone className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </a>
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50">
                Call {project.dialNumber.trim()}
              </div>
            </div>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            project.status === "active"
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              : project.status === "comingSoon"
              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              : project.status === "completed"
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
          }`}
        >
          {project.status?.charAt(0).toUpperCase() + project.status?.slice(1) || "N/A"}
        </span>
      </td>

      {/* Action Menu */}
      <td className="px-4 py-3 text-right">
        <div className="relative">
          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              toggleMenu(project.id, rect);
            }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <MoreVertical size={18} className="text-gray-600 dark:text-gray-300" />
          </button>

          {openMenuId === project.id && createPortal(
            <div 
              ref={menuRef}
              className="fixed w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[9999]"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              <button
                onClick={() => {
                  openForm(project);
                  setOpenMenuId(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg transition-colors"
              >
                <Edit2 size={16} className="text-blue-500" />
                Edit Project
              </button>
              <button
                onClick={() => openDeleteConfirm(project)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg transition-colors"
              >
                <Trash2 size={16} className="text-red-500" />
                Delete Project
              </button>
            </div>,
            document.body
          )}
        </div>
      </td>
    </tr>
  );
}

